import React, { useState, useEffect, useMemo } from 'react';
import Login from './login';

import { AppUser, CategoryKey } from './types';
import { CATEGORY_LABELS, MENU_ITEMS } from './constants';
import { HeroBanner } from './components/HeroBanner';
import { MenuCard } from './components/MenuCard';

//helper de chay ham lay avatar theo name
const getAvatarText = (name: string) => {
  const s = (name || "").trim();
  if (!s) return "US";

  const lastWord = s.split(/\s+/).filter(Boolean).at(-1) ?? "";

  const cleaned = lastWord
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "");

  return (cleaned.slice(0, 2) || "US").toUpperCase();
};

// Icon cho từng danh mục
const CATEGORY_ICONS: Record<CategoryKey, string> = {
  [CategoryKey.ALL]: 'fa-th-large',
  [CategoryKey.DOCUMENTS]: 'fa-file-lines',
  [CategoryKey.ERP]: 'fa-cubes',
  [CategoryKey.SERVICE]: 'fa-paper-plane',
  [CategoryKey.ADMIN]: 'fa-user-shield',
};

const App: React.FC = () => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  const [activeCategory, setActiveCategory] = useState<CategoryKey>(CategoryKey.ALL);
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer
  const [query, setQuery] = useState('');

  const loadMe = async () => {
    const res = await fetch('api/me.php', { credentials: 'include' });

    if (res.status === 401) {
      setUser(null);
      return;
    }

    const text = await res.text();
    let data: any = null;

    try {
      data = JSON.parse(text);
    } catch {
      console.error('me.php returned non-JSON:', text);
      setUser(null);
      return;
    }

    if (!data?.loggedIn) {
      setUser(null);
      return;
    }

    const u = data.user || {};
    const p = data.permissions || {};

    setUser({
      id: Number(u.id),
      username: u.username ?? null,
      name: u.name ?? null,
      role: u.role ?? 'user',
      isSuperAdmin: !!u.isSuperAdmin,
      permissions: {
        hasApprovalSeal: !!p.hasApprovalSeal,
        hasApprovalErp: !!p.hasApprovalErp,
        hasApprovalSftp: !!p.hasApprovalSftp,
        hasViewReport: !!p.hasViewReport,
        hasApprovalLeave: !!p.hasApprovalLeave,
        hasManageLeaveBalance: !!p.hasManageLeaveBalance,
        hasViewLeaveCalendar: !!p.hasViewLeaveCalendar,
      },
      pendingCounts: data.pendingCounts || {
        leave: 0,
        service: 0,
        erp: 0,
        sftp: 0,
      },
    });
  };

  const doLogout = async () => {
    try {
      await fetch('api/logout.php', {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
    } catch {
      // ignore
    } finally {
      window.location.reload();
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await loadMe();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredItems = useMemo(() => {
    if (!user) return [];

    const q = query.trim().toLowerCase();

    return MENU_ITEMS.filter((item) => {
      // super admin only
      if (item.isSuperAdminOnly && !user.isSuperAdmin) return false;

      // permission gated (super admin override)
      if (item.permission) {
        if (user.isSuperAdmin) return true;
        if (!user.permissions[item.permission]) return false;
      }

      // category filter
      if (activeCategory !== CategoryKey.ALL && item.category !== activeCategory) return false;

      // search filter
      if (q) {
        const hay = `${item.label} ${item.description || ''} ${item.category}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      return true;
    });
  }, [user, activeCategory, query]);

  if (loading) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-slate-100 space-y-6">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-slate-100 border-t-emerald-500 rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
          </div>
        </div>
        <p className="text-slate-400 font-extrabold tracking-[0.3em] text-[10px] uppercase">Portal NextGen 2026</p>
      </div>
    );
  }

  if (!user) return <Login />;
  const displayName = user.name || user.username || "User";
  const initials = getAvatarText(displayName);

  return (
    <div className="flex min-h-screen bg-stone-100 text-slate-900">
      {/* Sidebar Nav - Desktop */}
      <aside
        className="hidden lg:flex flex-col h-screen fixed left-0 top-0 bg-stone-900 border-r border-stone-800 
                   items-start py-8 space-y-12 shadow-2xl transition-all duration-300 ease-in-out z-50
                   group peer w-24 hover:w-64"
      >
        {/* Logo Section */}
        <div className="px-6 flex items-center gap-4 transition-all duration-300">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20 shrink-0">
            <span className="text-white font-black text-xl">S</span>
          </div>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 overflow-hidden whitespace-nowrap">
            <p className="font-black text-white tracking-tighter text-lg">SANGPHARMA</p>
            <p className="text-[10px] font-black tracking-widest uppercase text-emerald-500">Portal NextGen</p>
          </div>
        </div>

        <nav className="flex-1 flex flex-col items-start px-4 w-full space-y-4 overflow-y-auto overflow-x-hidden">
          <p className="px-4 text-[10px] font-black uppercase tracking-[0.2em] text-stone-500 mb-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
            Menu
          </p>

          {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
            const categoryKey = key as CategoryKey;

            // Nếu là danh mục Quản trị mà không phải Super Admin thì có thể ẩn (tùy chọn)
            // Tuy nhiên filteredItems đã lo việc lọc nội dung bên trong nên để hiện category vẫn ổn.

            return (
              <button
                key={key}
                onClick={() => setActiveCategory(categoryKey)}
                className={`w-full h-14 rounded-2xl flex items-center px-4 transition-all duration-300 relative group/btn ${activeCategory === categoryKey
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                  : 'text-stone-400 hover:bg-stone-800 hover:text-white'
                  }`}
              >
                <i className={`fas ${CATEGORY_ICONS[categoryKey]} text-xl shrink-0 w-6`}></i>
                <span className="ml-4 font-bold text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 overflow-hidden whitespace-nowrap">
                  {label}
                </span>
                {activeCategory === categoryKey && (
                  <div className="absolute left-[-16px] w-1.5 h-8 bg-emerald-400 rounded-r-full shadow-glow"></div>
                )}
              </button>
            );
          })}
        </nav>

        {/* Logout Section */}
        <div className="px-4 w-full mb-8">
          <button
            onClick={doLogout}
            className="w-full h-14 rounded-2xl flex items-center px-4 text-stone-400 hover:bg-rose-500/10 hover:text-rose-500 transition-all duration-300 overflow-hidden"
          >
            <i className="fas fa-sign-out-alt text-xl shrink-0 w-6"></i>
            <span className="ml-4 font-bold text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
              Đăng xuất
            </span>
          </button>
        </div>
      </aside>

      <main className="flex-1 min-h-screen flex flex-col lg:ml-24 lg:peer-hover:ml-64 transition-all duration-300 ease-in-out overflow-x-hidden">
        {/* Top Header */}
        <header className="sticky top-0 z-40 px-6 py-5 lg:px-12 bg-slate-50/80 backdrop-blur-xl border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-4 lg:hidden">
            <button onClick={() => setSidebarOpen(true)} className="text-slate-800 text-xl" title="Mở menu">
              <i className="fas fa-bars-staggered"></i>
            </button>
            <span className="font-black text-emerald-600 text-xl tracking-tighter">SANGPHARMA</span>
          </div>

          {/* Logo - Desktop */}
          <div className="hidden lg:flex items-center gap-3">
            <div className="leading-tight">
              <img src="images/logo.png" alt="Sang Pharma" className="w-20 h-25" />
            </div>
          </div>

          <div className="flex items-center gap-5">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-slate-900 font-black text-sm">{user.name || user.username || 'User'}</span>
              <span className="text-[10px] text-emerald-600 font-black uppercase tracking-widest">{user.role}</span>
            </div>

            <div className="w-11 h-11 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center cursor-pointer hover:border-emerald-500 transition-colors overflow-hidden">
              <div className="w-full h-full flex items-center justify-center bg-emerald-500">
                <span className="text-white font-black text-sm tracking-wide select-none">
                  {initials}
                </span>
              </div>
            </div>

          </div>
        </header>

        {/* Mobile drawer */}
        <div
          className={`fixed inset-0 z-50 lg:hidden transition ${sidebarOpen ? 'pointer-events-auto' : 'pointer-events-none'
            }`}
        >
          <div
            className={`absolute inset-0 bg-black/30 transition-opacity ${sidebarOpen ? 'opacity-100' : 'opacity-0'
              }`}
            onClick={() => setSidebarOpen(false)}
          />
          <div
            className={`absolute left-0 top-0 h-full w-[82%] max-w-sm bg-white shadow-2xl transition-transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
              }`}
          >
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500 rounded-2xl flex items-center justify-center shadow-sm">
                  <span className="text-white font-black text-lg">S</span>
                </div>
                <div>
                  <p className="font-black text-slate-900">SANGPHARMA</p>
                  <p className="text-[10px] font-black tracking-widest uppercase text-slate-400">Portal</p>
                </div>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="text-slate-700 text-xl" title="Đóng">
                <i className="fas fa-xmark" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-slate-50 border border-slate-100 rounded-2xl px-4 py-3 flex items-center gap-3">
                <i className="fas fa-search text-slate-300" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Tìm nhanh..."
                  className="bg-transparent outline-none w-full text-sm font-medium text-slate-700 placeholder:text-slate-300"
                />
              </div>

              <div className="space-y-2">
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => {
                      setActiveCategory(key as CategoryKey);
                      setSidebarOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-2xl font-black text-sm border transition ${activeCategory === key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-100'
                      }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <button
                onClick={doLogout}
                className="w-full px-4 py-3 rounded-2xl font-black text-sm bg-rose-50 text-rose-600 border border-rose-100"
              >
                <i className="fas fa-sign-out-alt mr-2" />
                Đăng xuất
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-7xl mx-auto px-6 py-12 lg:px-12 w-full">
          <HeroBanner name={user.name || user.username || 'Thành viên'} />

          {/* Category Filter */}
          <div className="mb-5 mt-5 flex justify-center lg:justify-start">
            <div className="flex items-center p-1.5 bg-slate-50 rounded-2xl border border-slate-200 shadow-sm w-fit overflow-x-auto max-w-full">
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setActiveCategory(key as CategoryKey)}
                  className={`px-7 py-3 rounded-xl text-xs font-black transition-all whitespace-nowrap ${activeCategory === key ? 'bg-slate-900 text-white shadow-xl shadow-slate-900/10' : 'text-slate-400 hover:text-slate-800'
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Flat Grid View (Original) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-20">
            {filteredItems.map((item) => {
              let badgeCount = 0;
              if (user?.pendingCounts) {
                switch (item.id) {
                  case 'approve_leave':
                    badgeCount = user.pendingCounts.leave;
                    break;
                  case 'approve_seal':
                    badgeCount = user.pendingCounts.service;
                    break;
                  case 'approve_erp':
                    badgeCount = user.pendingCounts.erp;
                    break;
                  case 'approve_sftp':
                    badgeCount = user.pendingCounts.sftp;
                    break;
                  case 'approve_leave_cancel':
                    badgeCount = user.pendingCounts.leave;
                    break;
                }
              }

              return (
                <MenuCard
                  key={item.id}
                  item={item}
                  badgeCount={badgeCount > 0 ? badgeCount : undefined}
                />
              );
            })}

            {filteredItems.length === 0 && (
              <div className="col-span-full py-28 text-center bg-slate-50 rounded-[3rem] border-2 border-dashed border-slate-200">
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-inbox text-3xl text-slate-200"></i>
                </div>
                <h3 className="text-slate-800 font-bold text-lg">Không có dữ liệu</h3>
                <p className="text-slate-400 font-medium">Hiện không có tính năng nào phù hợp với tìm kiếm của bạn.</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-auto px-12 py-10 border-t border-slate-200 bg-slate-50 text-center flex flex-col md:flex-row items-center justify-center">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-xs">S</span>
            </div>
            <p className="text-slate-400 text-[11px] font-black tracking-[0.2em] uppercase">
              &copy; 2026 Sang Pharma. All rights reserved.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default App;
